import * as actions from '../../../ActionsConst';
import { showToast, createToastId } from '../toast/ToastActions';
import { productChange } from '../../../products/actions/change/ChangeActions';
import { productSettings } from '../../../products/actions/settings/SettingsActions';
import { productsApiCall } from '../../../products/actions/products/ProductsActions';
import { productDeselectAllAction } from '../../../products/actions/select-multiple/SelectMultipleActions';
import toastMsg from '../../../../data/toast';
import { fetchUser } from '../../../config/ConfigActions';
import AnalyticsClient from '../../../../clients/AnalyticsClient';
import productEvents from '../../../../analytics/events/products';
import CartServiceClient from '../../../../clients/CartService/CartServiceClient';

export const couldNotAddToCart = (selectedProducts) => (dispatch) => {
  const toast = showToast({
    error: true,
    id: createToastId(),
    message: {
      // Sorry - none of the product(s) you selected are available in the current store.
      messageDescriptor: toastMsg.couldNotAddToCart,
      values: { selectedProducts },
    },
  });
  dispatch(toast);
};

export const cartUpdate = (count) => ({
  type: actions.CART_UPDATE,
  count,
});

const getAddSuccessToast = ({ succeeded }) =>
  showToast({
    id: createToastId(),
    message: {
      // You have added {addedCount} products to your cart
      messageDescriptor: toastMsg.addToCartTotalSuccessMessage,
      values: { addedCount: succeeded },
    },
  });

const getAddNoSuccessToast = ({ failed }) =>
  showToast({
    error: true,
    id: createToastId(),
    message: {
      // Sorry the {failedCount} products you selected couldn't be added to the cart
      messageDescriptor: toastMsg.addToCartTotalErrorMessage,
      values: { failedCount: failed },
    },
  });

const getAddPartialSuccessToast = ({ succeeded, failed }) =>
  showToast({
    id: createToastId(),
    message: {
      // You have added {addedCount} product(s) to your cart, however {failedCount} products couldn't be added
      messageDescriptor: toastMsg.addToCartPartialSuccessMessage,
      values: { addedCount: succeeded, failedCount: failed },
    },
  });

export const getAppropriateAddSuccessToast = (result: { failed: number, succeeded: number }) => {
  if (result.failed === 0) {
    return getAddSuccessToast(result);
  }
  if (result.succeeded === 0) {
    return getAddNoSuccessToast(result);
  }
  return getAddPartialSuccessToast(result);
};

const getAddErrorToast = (count: number) =>
  showToast({
    error: true,
    id: createToastId(),
    // Sorry - we couldn't add those items to your cart.
    message: {
      messageDescriptor: toastMsg.addToCartCommonErrorMessage,
      values: { selectedProducts: count },
    },
  });

export const cartGetRequest = (baseApiUrl) => (dispatch) => {
  return fetchUser(baseApiUrl).then((user) => {
    CartServiceClient.getCartCount(baseApiUrl, user.selectedStoreCode)
      .then((count) => dispatch(cartUpdate(count)))
      .catch((err) => {
        dispatch(getAddErrorToast(0));
        throw new Error(`Cart retrieval error:: ${err}`);
      });
  });
};

const isSkippedProduct = (product) => product.isChecked && !product.isDeleted && !product.availability.isAvailable;
const getAddToCartCountResult = (result, state, selectAll) => {
  if (selectAll) {
    return result;
  }
  const skippedProductCount = state.products.filter(isSkippedProduct).length;
  return { ...result, failed: result.failed + skippedProductCount };
};

const cartSingle = (approvalStatus) => ({ jsonResponse, error, ids, store }) => {
  const { dispatch } = store;
  const productId = ids[0];
  if (error) {
    dispatch(productChange(productId, { isAdded: false, isAdding: false }));
    dispatch(getAddErrorToast(1));
    return;
  }
  dispatch(cartUpdate(jsonResponse.count));
  dispatch(getAppropriateAddSuccessToast(jsonResponse));
  dispatch(productChange(productId, { isAdded: true, isAdding: false }));
  dispatch(productDeselectAllAction());

  AnalyticsClient.trackEvent(productEvents.singleAddToCart(approvalStatus));
};

const cartMultiple = ({ jsonResponse, error, ids, store }) => {
  const { dispatch } = store;
  if (error) {
    const totalProducts = store.getState().productSettings.total;
    dispatch(productSettings({ isAdding: false, isAdded: false }));
    dispatch(getAddErrorToast(ids.length || totalProducts));
    return;
  }
  const resultCount = getAddToCartCountResult(jsonResponse, store.getState(), ids.length === 0);
  dispatch(cartUpdate(jsonResponse.count));
  dispatch(getAppropriateAddSuccessToast(resultCount));
  dispatch(productSettings({ isAdding: false, isAdded: true }));
  dispatch(productDeselectAllAction());

  AnalyticsClient.trackEvent(productEvents.bulkAddToCart());
};

export const cartRequest = ({ ids, excludedIds, isBulkMode, approvalStatus }) => (dispatch) => {
  dispatch(
    productsApiCall({
      ids,
      excludedIds,
      method: 'POST',
      url: 'cart',
      urlConfig: {
        sendIds: ids.length > 0,
        sendPaging: false,
        sendFilters: ids.length === 0, // Bulk action requires filters
        sendStore: true,
      },
      callback: isBulkMode ? cartMultiple : cartSingle(approvalStatus),
    }),
  );
};
